import {useNavigate, useParams} from "react-router-dom";
import {useTranslation} from "react-i18next";
import {useBoundStore} from "../../Store/store";
import {useEffect, useState} from "react";
import {BaseCard} from "../../Components/Cards/BaseCard";
import * as React from "react";
import {ShiftInfoCard} from "../../Components/Cards/ShiftInfoCard";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faArrowLeft} from "@fortawesome/free-solid-svg-icons";
import dayjs from "dayjs";
import PhoneCheck from "../../Assets/images/illustrations/phone_check.png";
import { STATUUT_STYLES } from "../../Utils/StatuutHelper";

export const MyApplicationDetail = () => {
    const {id} = useParams();
    const navigate = useNavigate();
    const {t} = useTranslation();

    const getApplicationDetail = useBoundStore(state => state.getApplicationDetail);
    const applicationDetail = useBoundStore(state => state.applicationDetail);
    const withdrawApplication = useBoundStore(state => state.withdrawApplication);
    const user = useBoundStore(state => state.user);
    const isMultiStatuut = (user?.extra_statuten?.length ?? 0) > 0;

    const [showWithdrawConfirmation, setShowWithdrawConfirmation] = useState(false);
    const [isWithdrawing, setIsWithdrawing] = useState(false);

    useEffect(() => {
        getApplicationDetail(id);
    }, [getApplicationDetail, id]);

    const handleWithdrawApplication = async () => {
        setIsWithdrawing(true);
        try {
            const success = await withdrawApplication(id);
            if (success) {
                setShowWithdrawConfirmation(false);
                navigate(-1);
            }
        } finally {
            setIsWithdrawing(false);
        }
    };

    return (
        <div className="px-4 md:px-0">
            <div className={'flex flex-row justify-between mb-4 items-center'}>
                <div>
                    <h1 className={'text-2xl font-semibold'}>{t('applications.detail_title')}</h1>
                    <div className={"flex flex-row group items-center cursor-pointer"}
                         onClick={() => navigate(-1)}>
                        <FontAwesomeIcon icon={faArrowLeft}
                                         className={'text-sm transition-all group-hover:mr-2 duration-500'}/>
                        <div className={'text-sm text-gray-500 ml-2'}>
                            {t('general.actions.back_to_overview')}
                        </div>
                    </div>
                </div>

                {((applicationDetail?.status === 'pending' || applicationDetail?.status === 'applied') && !applicationDetail?.reserved_at) && (
                    <button
                        onClick={() => setShowWithdrawConfirmation(true)}
                        className="rounded-md border border-solid border-red-600 cursor-pointer px-4 py-2 text-sm font-semibold shadow-sm transition-all duration-300 ease-in-out bg-red-600 text-white hover:bg-red-700 hover:border-red-700"
                    >
                        {t('applications.withdraw_application')}
                    </button>
                )}
            </div>

            <div className={'grid grid-cols-1 md:grid-cols-3 gap-4'}>
                <div className={'md:col-span-2'}>
                    <BaseCard border={false}>
                        <h2 className="text-lg text-gray-700 uppercase mb-4">{t('general.labels.global_information')}</h2>
                        <div className="space-y-2">
                            <ReadOnlyField label={t('general.labels.status')} value={
                                applicationDetail?.status === "accepted" ? t('general.status.accepted') :
                                applicationDetail?.status === "rejected" ? t('general.status.rejected') :
                                applicationDetail?.status === "pending" ? t('general.status.pending') :
                                applicationDetail?.status === "applied" ? t('general.status.applied') :
                                applicationDetail?.status
                            }/>
                            <ReadOnlyField
                                label={t('applications.applied_at')}
                                value={
                                    applicationDetail?.applied_at?.locale
                                        ? dayjs(applicationDetail.applied_at.locale).format('DD/MM/YYYY HH:mm')
                                        : '-'
                                }
                            />
                            <ReadOnlyField
                                label={t('applications.accepted_at')}
                                value={
                                    applicationDetail?.accepted_at?.locale
                                        ? dayjs(applicationDetail.accepted_at.locale).format('DD/MM/YYYY HH:mm')
                                        : '-'
                                }
                            />
                        </div>
                    </BaseCard>
                </div>

                <BaseCard border={false}>
                    <ShiftInfoCard
                        shift={applicationDetail?.shift}
                        overrideImage={PhoneCheck}
                    />
                    {isMultiStatuut && applicationDetail?.applied_as_statuut && (
                        <div className="mt-4 pt-4 border-t border-gray-200">
                            <ReadOnlyField
                                label={t('applications.applied_as_statuut')}
                                value={STATUUT_STYLES[applicationDetail.applied_as_statuut]?.label ?? applicationDetail.applied_as_statuut}
                            />
                        </div>
                    )}
                </BaseCard>
            </div>

            {showWithdrawConfirmation && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">
                            {t('applications.withdraw_application')}
                        </h3>
                        <p className="text-gray-600 mb-6">
                            {t('applications.withdraw_confirmation')}
                        </p>
                        <div className="flex gap-4 justify-end">
                            <button
                                onClick={() => setShowWithdrawConfirmation(false)}
                                disabled={isWithdrawing}
                                className="rounded-md border border-solid border-gray-300 cursor-pointer px-4 py-2 text-sm font-semibold shadow-sm transition-all duration-300 ease-in-out bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {t('general.actions.cancel')}
                            </button>
                            <button
                                onClick={handleWithdrawApplication}
                                disabled={isWithdrawing}
                                className="rounded-md border border-solid border-red-600 cursor-pointer px-4 py-2 text-sm font-semibold shadow-sm transition-all duration-300 ease-in-out bg-red-600 text-white hover:bg-red-700 hover:border-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {isWithdrawing ? t('applications.withdrawing') : t('applications.confirm_withdraw')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

const ReadOnlyField = ({label, value}) => (
    <div className="flex flex-col">
        <label className="text-sm text-gray-500">{label}</label>
        <div className="text-base text-gray-800">{value !== null && value !== undefined ? value : '-'}</div>
    </div>
);
